/** The tenant's members (E12-T09): invite by email, change a role, remove. A tenant keeps an owner:
 * the API refuses otherwise, and says so. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useMe } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Member = Schemas["MemberOut"];
type Role = Member["role"];
export const ROLES: Role[] = ["owner", "admin", "developer", "operator", "viewer", "billing"];
const FIELD =
  "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm disabled:opacity-60";

function useMembersChanged() {
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  return () => queries.invalidateQueries({ queryKey: ["members", tenant] });
}

export function Members({ members }: { members: Member[] }) {
  const t = useT();
  const me = useMe();
  return (
    <div className="space-y-4">
      {members.length === 0 ? (
        <Empty title={t("settings.membersEmpty")} />
      ) : (
        <Table
          caption={t("settings.members")}
          rows={members}
          rowKey={(m) => m.user_id}
          columns={[
            { header: t("settings.col.email"), cell: (m) => m.email },
            { header: t("settings.col.name"), cell: (m) => m.name },
            { header: t("settings.col.role"), cell: (m) => <RoleSelect member={m} self={m.user_id === me.user.id} /> },
            { header: t("settings.col.joined"), cell: (m) => (m.joined ? t("common.yes") : t("settings.invited")) },
            { header: t("settings.col.actions"), cell: (m) => <Remove member={m} self={m.user_id === me.user.id} /> },
          ]}
        />
      )}
      <Invite />
    </div>
  );
}

function RoleSelect({ member, self }: { member: Member; self: boolean }) {
  const t = useT();
  const { api } = useBff();
  const changed = useMembersChanged();
  const change = useMutation({
    mutationFn: (role: Role) =>
      data(api.PATCH("/v1/members/{user_id}", { params: { path: { user_id: member.user_id } }, body: { role } })),
    onSuccess: changed,
  });
  return (
    <div>
      <select
        aria-label={t("settings.roleOf", { email: member.email })}
        value={member.role}
        disabled={change.isPending}
        onChange={(e) => change.mutate(e.target.value as Role)}
        className={FIELD}
      >
        {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
      </select>
      {self && <span className="ml-2 text-xs text-[var(--color-muted)]">{t("settings.you")}</span>}
      {change.isError && <ErrorState error={change.error} />}
    </div>
  );
}

function Remove({ member, self }: { member: Member; self: boolean }) {
  const t = useT();
  const { api } = useBff();
  const changed = useMembersChanged();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/members/{user_id}", { params: { path: { user_id: member.user_id } } })),
    onSuccess: changed,
  });
  if (!confirming) {
    return (
      <Button variant="ghost" onClick={() => setConfirming(true)} aria-label={t("settings.removeWho", { email: member.email })}>
        {t("settings.remove")}
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm">{t(self ? "settings.removeSelf" : "settings.removeConfirm")}</span>
      <Button variant="secondary" disabled={remove.isPending} onClick={() => remove.mutate()}>
        {t("settings.removeYes")}
      </Button>
      <Button variant="ghost" onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
      {remove.isError && <ErrorState error={remove.error} />}
    </div>
  );
}

function Invite() {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const changed = useMembersChanged();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("developer");
  const invite = useMutation({
    mutationFn: () => data(api.POST("/v1/members", { body: { email: email.trim(), role } })),
    onSuccess: async () => {
      setEmail("");
      await changed();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) invite.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-sm font-medium">{t("settings.invite")}</h3>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-email`} className="block text-xs text-[var(--color-muted)]">{t("settings.col.email")}</label>
          <input id={`${id}-email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className={`${FIELD} w-72`} autoComplete="off" />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-role`} className="block text-xs text-[var(--color-muted)]">{t("settings.col.role")}</label>
          <select id={`${id}-role`} value={role} onChange={(e) => setRole(e.target.value as Role)} className={FIELD}>
            {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
          </select>
        </div>
        <Button type="submit" disabled={invite.isPending}>{t("settings.inviteSend")}</Button>
      </div>
      <p className="text-xs text-[var(--color-muted)]">{t("settings.inviteHint")}</p>
      {invite.isSuccess && <p role="status" className="text-sm">{t("settings.invitedWho", { email: invite.data.email })}</p>}
      {invite.isError && <ErrorState error={invite.error} />}
    </form>
  );
}
